import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { authApi } from "../api/auth.api";
import { IAdmin, IAdminState, IUserAuthResponse } from "../types/auth.types";
import { deleteCookie, getCookie, setCookie } from "../../utils/token";
import { userApi } from "../api/user.api";
import { TRRMListEntry, TMultyCompanyDetails } from "../types/user.types";
import { tWithKeyAttr as t } from "../../i18nHelper";
import { utilsApi } from "../api/utils.api";
import { ICompanyDetails } from "../types/utils.types";
import { TCompany } from "../types/users.types";
import { showUnauthorizedModal } from "../actions/auth";
import {
  clearPostAuthRedirect,
  readIsFirst,
  stashIsFirstRedirect,
} from "../../utils/postLoginRedirect";

const accessToken = getCookie("accessToken");
const refreshToken = getCookie("refreshToken");

const initialUser: IAdmin = {
  id: "",
  firstName: "",
  lastName: "",
  fullName: "",
  account: "",
  avatar: "",
  email: "",
  // users: [],
  // hosenUsers: [],
  // hosenUsersWithRecommendations: [],
  // therapists: [],
  companyId: 0,
  fakeCompanyId: null,
  usersMultipleCompanies: [],
  selectedCompany: null,
  companyName: undefined,
  companyLogoUrl: undefined,
};

const initialState: IAdminState = {
  admin: initialUser,
  accessToken: accessToken,
  refreshToken: refreshToken,
  error: null,
  isAuth: false,
  RRMList: [],
  isChargingCustomers: false,
  therapistsCanSeePrices: false,
  canAddDefaultReports: null,
  adminCanViewPatientRecordings: null,
  deviceViewEnabled: null,
  showUnauthorizedModal: false,
  companyGuid: null,
};

const storeUser = (
  state: IAdminState,
  { payload }: { payload: { user: IAdmin } }
) => {
  for (let key in payload.user) {
    state.admin[key.charAt(0).toLowerCase() + key.slice(1)] = payload.user[key];
  }
};
const storeTokens = (state: IAdminState, { payload }: any) => {
  if (readIsFirst(payload)) {
    stashIsFirstRedirect(true);
  }
  const accessToken = payload.accessToken ?? payload.AccessToken;
  if (!accessToken) {
    return;
  }
  if (!readIsFirst(payload)) {
    clearPostAuthRedirect();
  }
  state.accessToken = accessToken;
  state.isAuth = true;
  state.showUnauthorizedModal = false;
  // state.refreshToken = refreshToken;
  setCookie("accessToken", accessToken, {
    //cookie expire on 12am.
    expires: (() => {
      const currentTime = new Date();
      const nextDay12AM = new Date(currentTime);
      nextDay12AM.setDate(currentTime.getDate() + 1);
      nextDay12AM.setHours(0, 0, 0, 0);
      //set cookie multiply by 1000 hence we divide by 1000.
      return (nextDay12AM.getTime() - currentTime.getTime()) / 1000;
    })(),
  });
  const { user } = payload;
  if (user) {
    storeUser(state, { payload });
    // state.isAuth = true;
  }
  state.error = payload.success ? null : payload.message;
  // state.isAuth = payload.success ? payload.success : false;
  // if (state.refreshToken) {
  //   setCookie("refreshToken", state.refreshToken);
  // }
};

const storeErrors = (state: IAdminState, { payload }: any) => {
  const { data } = payload;
  state.error =
    typeof data?.message === "string"
      ? data?.message
      : t('SOMETHING_WENT_WRONG_TRY_AGAIN_LATER');
};

export const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setPhone: (state, { payload }) => {
      return { ...state, admin: { ...state.admin, phone: payload } };
    },
    setLoginInfo: (
      state,
      {
        payload,
      }: { payload: { email: string; password: string; id: string | number } }
    ) => {
      return {
        ...state,
        admin: {
          ...state.admin,
          email: payload.email,
          password: payload.password,
          id: payload.id,
        },
      };
    },
    clearRetryAfter: (state) => {
      return {
        ...state,
        retryAfterTimestamp: null,
      };
    },
    setCompanyId: (state, payload: { payload: number }) => {
      return {
        ...state,
        admin: { ...state.admin, companyId: payload.payload },
      };
    },
    setSelectedCompany: (state, { payload }: { payload: TMultyCompanyDetails | null }) => {
      return {
        ...state,
        selectedCompany: payload,
        admin: {
          ...state.admin,
          companyId: payload?.CompanyId ?? 0,
          companyName: payload?.CompanyName ?? undefined,
          companyLogoUrl: payload?.ImageUrl ?? undefined,
        },
      };
    },
    setMultyCompaniesList: (state, { payload }: { payload: TMultyCompanyDetails[] }) => {
      return {
        ...state,
        multyCompaniesList: payload,
      };
    },
    updateAdminFields: (state, { payload }: { payload: Partial<IAdmin> }) => {
      return {
        ...state,
        admin: {
          ...state.admin,
          ...payload,
        },
      };
    },
    logout: () => {
      deleteCookie("refreshToken");
      deleteCookie("accessToken");
      return initialState;
    },
    clearUnauthorizedModal: (state) => {
      state.showUnauthorizedModal = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(showUnauthorizedModal, (state, action) => {
        state.showUnauthorizedModal = action.payload;
      })
      .addMatcher(authApi.endpoints.login.matchFulfilled, storeTokens)
      .addMatcher(authApi.endpoints.verifySsoToken.matchFulfilled, storeTokens)
      .addMatcher(authApi.endpoints.redeemSwitchCode.matchFulfilled, storeTokens)
      .addMatcher(
        authApi.endpoints.login.matchRejected,
        (state: IAdminState, { payload }: any) => {
          const { data, status } = payload;
          if (status === 429) {
            state.error = "יותר מדי ניסיונות התחברות. אנא נסו שוב מאוחר יותר.";
            state.retryAfterTimestamp = Date.now() + 60 * 1000;
          } else {
            state.error =
              typeof data?.message === "string"
                ? data?.message
                : t('SOMETHING_WENT_WRONG_TRY_AGAIN_LATER');
          }
        }
      )
      .addMatcher(
        userApi.endpoints.getRRMList.matchFulfilled,
        (state: IAdminState, { payload }: { payload: TRRMListEntry[] }) => {
          state.RRMList = payload;
        }
      )
      .addMatcher(authApi.endpoints.loginGoogle.matchFulfilled, storeTokens)
      .addMatcher(authApi.endpoints.loginGoogle.matchRejected, storeErrors)
      .addMatcher(
        authApi.endpoints.loginWithCode.matchFulfilled,
        (state, action) => {
          state.error = action.payload.success ? null : action.payload.message;
        }
      )
      .addMatcher(authApi.endpoints.loginWithCode.matchRejected, storeErrors)
      .addMatcher(
        authApi.endpoints.authWithCode.matchFulfilled,
        (state, action: PayloadAction<IUserAuthResponse>) => {
          if (action.payload.accessToken) {
            setCookie("accessToken", action.payload.accessToken, {
              expires: (() => {
                const currentTime = new Date();
                const nextDay12AM = new Date(currentTime);
                nextDay12AM.setDate(currentTime.getDate() + 1);
                nextDay12AM.setHours(0, 0, 0, 0);
                return (nextDay12AM.getTime() - currentTime.getTime()) / 1000;
              })(),
            });
            state.accessToken = action.payload.accessToken;
          }
          state.isAuth = true;
          storeUser(state, action);
          state.admin.password = "";
        }
      )
      .addMatcher(authApi.endpoints.authWithCode.matchRejected, storeErrors)
      .addMatcher(authApi.endpoints.authWithoutLogin.matchRejected, storeErrors)
      .addMatcher(
        authApi.endpoints.authWithoutLogin.matchFulfilled,
        (state, action) => {
          storeTokens(state, action);
          storeUser(state, action);
          state.error = action.payload.success ? null : action.payload.message;
          state.isAuth = true;
        }
      )
      .addMatcher(
        authApi.endpoints.sendCodeWithoutLogin.matchRejected,
        storeErrors
      )
      .addMatcher(
        authApi.endpoints.sendCodeWithoutLogin.matchFulfilled,
        (store) => {
          store.error = null;
        }
      )
      .addMatcher(userApi.endpoints.getUser.matchFulfilled, (state, action) => {
        storeUser(state, action);
        state.error = action.payload.success ? null : action.payload.message;
        state.isAuth = true;
      })
      .addMatcher(
        authApi.endpoints.SwitchCompanyUser.matchFulfilled,
        (state: IAdminState, { payload }: { payload: { accessToken: string; success: boolean } }) => {
          if (payload.accessToken) {
            state.accessToken = payload.accessToken;
            deleteCookie("accessToken");
            setCookie("accessToken", payload.accessToken, {
              expires: (() => {
                const currentTime = new Date();
                const nextDay12AM = new Date(currentTime);
                nextDay12AM.setDate(currentTime.getDate() + 1);
                nextDay12AM.setHours(0, 0, 0, 0);
                return (nextDay12AM.getTime() - currentTime.getTime()) / 1000;
              })(),
            });
          }
        }
      )
      .addMatcher(
        utilsApi.endpoints.getCompanyById.matchFulfilled,
        (state: IAdminState, { payload }: { payload: ICompanyDetails }) => {
          state.isChargingCustomers = payload.isChargingCustomers;
          state.therapistsCanSeePrices = payload.therapistsCanSeePrices;
          state.companyGuid = payload.guid;
          //state.isChargingCustomers = true; //for tests
          
          // Store company name and logo in admin state
          if (payload.id === state.admin.companyId) {
            state.admin.companyName = payload.name;
            state.admin.companyLogoUrl = payload.logoUrl;
            state.canAddDefaultReports = payload.canAddDefaultReports;
            state.adminCanViewPatientRecordings = payload.adminCanViewPatientRecordings ?? false;
            state.deviceViewEnabled = payload.deviceViewEnabled ?? false;
          }
        }
      )
      .addMatcher(
        utilsApi.endpoints.changeTherapistsCanSeePrices.matchFulfilled,
        (state: IAdminState, { payload }: { payload: TCompany }) => {
          state.therapistsCanSeePrices = payload.therapistsCanSeePrices ?? false;
        }
      )
      .addMatcher(
        utilsApi.endpoints.changeCanAddDefaultReports.matchFulfilled,
        (state: IAdminState, { payload }: { payload: TCompany }) => {
          state.canAddDefaultReports = payload.canAddDefaultReports ?? false;
        }
      )
      .addMatcher(
        utilsApi.endpoints.changeAdminCanViewPatientRecordings.matchFulfilled,
        (state: IAdminState, { payload }: { payload: TCompany }) => {
          state.adminCanViewPatientRecordings = payload.adminCanViewPatientRecordings ?? false;
        }
      )
      .addMatcher(
        userApi.endpoints.updateUser.matchFulfilled,
        (state: IAdminState, { meta }: any) => {
          const updates = meta?.arg?.originalArgs?.body;
          if (updates && Array.isArray(updates)) {
            updates.forEach((update: any) => {
              if (update.path && update.value !== undefined) {
                const fieldName = update.path.replace('/', '');
                state.admin[fieldName] = update.value;
              }
            });
          }
        }
      )
      .addMatcher(
        userApi.endpoints.updateTherapistInfo.matchFulfilled,
        (state: IAdminState, { meta }: any) => {
          const { about, video } = meta?.arg?.originalArgs || {};
          if (about !== undefined) {
            state.admin.about = about;
          }
          if (video !== undefined) {
            state.admin.video = video;
          }
        }
      )
      .addMatcher(
        userApi.endpoints.deleteUserAvatar.matchFulfilled,
        (state: IAdminState, { meta }: any) => {
          const { guid } = meta?.arg?.originalArgs || {};
          if (guid === state.admin.guid) {
            state.admin.avatar = "";
          }
        }
      )
      .addMatcher(authApi.endpoints.logout.matchFulfilled, (state, action) => {
        deleteCookie("refreshToken");
        deleteCookie("accessToken");
        state.admin = initialUser;
        state.accessToken = "";
        state.refreshToken = "";
        state.error = action.payload.success ? null : action.payload.message;
        state.isAuth = false;
      });
  },
});

export const { 
  setPhone, 
  setLoginInfo, 
  clearRetryAfter, 
  setCompanyId, 
  setSelectedCompany, 
  setMultyCompaniesList,
  updateAdminFields,
  logout 
} = userSlice.actions;
export const { actions, reducer } = userSlice;
export default userSlice.reducer;
